/**
 * Death shatter (spec 3.3): the ship bursts into ~28 emissive shards that
 * fly, tumble, and fade over ~600 ms. No gore, no full-screen flash
 * (photosensitivity, spec 7). Render-side randomness only. Disabled on the
 * Low tier and under reduced motion.
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { SolidParticleSystem } from "@babylonjs/core/Particles/solidParticleSystem";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { makeFlatMaterial } from "./scene";

const SHARDS = 28;
const LIFETIME_S = 0.6;

export class DeathFx {
  private sps: SolidParticleSystem | null = null;
  private mesh: Mesh | null = null;
  private age = 0;
  private active = false;
  private enabled = true;

  constructor(private readonly scene: Scene) {}

  setEnabled(on: boolean): void {
    this.enabled = on;
  }

  /** Small spark burst (head-bump feedback, R2 §2.1). */
  spark(x: number, y: number, z: number): void {
    this.burst(x, y, z, "#FFF7D6", 8, 0.07);
  }

  /** Finish celebration (R2-M4 VFX-5): a fountain of palette-colored shards
   *  that hang in the air while the camera orbits — festive, not violent. */
  confetti(x: number, y: number, z: number, colorHex: string): void {
    this.burst(x, y, z, colorHex, 44, 0.13, { up: true, lifetimeS: 1.9 });
  }

  burst(
    x: number, y: number, z: number,
    colorHex: string,
    count = SHARDS,
    size = 0.16,
    opts?: { up?: boolean; lifetimeS?: number },
  ): void {
    if (!this.enabled) return;
    this.disposeCurrent();
    this.lifetime = opts?.lifetimeS ?? LIFETIME_S;
    const up = opts?.up ?? false;
    this.sps = new SolidParticleSystem("death-shards", this.scene, { updatable: true });
    const shard = CreateBox("shard", { size }, this.scene);
    this.sps.addShape(shard, count);
    shard.dispose();
    this.mesh = this.sps.buildMesh();
    const mat = makeFlatMaterial(this.scene, "mat-shards", colorHex, 1.2);
    this.mesh.material = mat;
    this.mesh.isPickable = false;

    this.sps.initParticles = () => {
      for (const p of this.sps!.particles) {
        p.position.set(x, y + 0.3, z);
        if (up) {
          // confetti: mostly vertical, gentle spread, floats down slowly
          p.velocity.set(
            (Math.random() - 0.5) * 5,
            Math.random() * 5 + 4,
            (Math.random() - 0.5) * 5,
          );
        } else {
          p.velocity.set(
            (Math.random() - 0.5) * 9,
            Math.random() * 6 + 1.5,
            (Math.random() - 0.5) * 6,
          );
        }
        p.rotation.set(Math.random() * 3, Math.random() * 3, Math.random() * 3);
      }
    };
    const gravity = up ? 7 : 22;
    this.sps.updateParticle = (p) => {
      p.velocity.y -= gravity * this.dt;
      p.position.addInPlace(p.velocity.scale(this.dt));
      p.rotation.x += 6 * this.dt;
      p.rotation.z += 5 * this.dt;
      return p;
    };
    this.sps.initParticles();
    this.sps.setParticles();
    this.age = 0;
    this.active = true;
  }

  private dt = 1 / 60;
  private lifetime = LIFETIME_S;

  /** Advance the burst; call every frame (cheap no-op when inactive). */
  update(dtSeconds: number): void {
    if (!this.active || !this.sps || !this.mesh) return;
    this.dt = Math.min(dtSeconds, 0.05);
    this.age += this.dt;
    this.sps.setParticles();
    (this.mesh.material as StandardMaterial).alpha = Math.max(0, 1 - this.age / this.lifetime);
    if (this.age >= this.lifetime) this.disposeCurrent();
  }

  private disposeCurrent(): void {
    this.mesh?.material?.dispose();
    this.sps?.dispose();
    this.sps = null;
    this.mesh = null;
    this.active = false;
  }
}
